// =============================================================================
// LIGA AJUPAM — Recuperación de chunks obsoletos tras un redeploy
// =============================================================================
// Cuando pusheamos a main, Vercel redeploya y los assets JS cambian de hash. Un
// usuario con la app vieja abierta que navega a una pantalla lazy puede pegar:
//   - "Failed to fetch dynamically imported module" (el chunk ya no existe), o
//   - "undefined is not an object (evaluating 'e.TeamView')" (el import resolvió
//      pero el chunk viejo no tiene ese export), o
//   - "useEffect is not defined" (el vendor-react cargado es de otro build).
// Todos son transitorios: se arreglan recargando para tomar los assets nuevos.
// Este módulo centraliza esa recuperación (un solo guard anti-loop compartido por
// el handler de vite:preloadError y el onError del ErrorBoundary raíz).
// =============================================================================

const RELOAD_TS_KEY = 'ajupam_preload_reload_ts';
// v6.11.74: timestamp de la PRIMERA recarga de la tanda. Va aparte de
// RELOAD_TS_KEY —que se pisa en cada etapa para medir la ventana anti-loop—
// porque el diagnóstico que sube a Sentry necesita cuánto duró la recuperación
// ENTERA, no el hueco entre las dos últimas etapas. Ver sinceFirstReloadMs.
const RELOAD_FIRST_TS_KEY = 'ajupam_preload_reload_first_ts';
const RELOAD_STAGE_KEY = 'ajupam_preload_reload_stage';
const RELOAD_WINDOW_MS = 30_000;

/**
 * v6.11.186 — Espera antes del ÚLTIMO intento (etapa 3).
 *
 * Hasta acá la recuperación eran tres cargas de página seguidas, sin pausa, y
 * se rendía en la tercera. En iPhone eso duraba ~14 segundos desde que se abría
 * la app: los reintentos dentro de la página (lazyWithReload) no vuelven a la
 * red en WebKit —en LIGA-AJUPAM-51 del 14/09 el 2º y 3er intento fallaron 2 ms y
 * 29 ms después de su espera—, así que lo único que reintenta de verdad es
 * recargar, y tres recargas pegadas caen enteras dentro del mismo bache.
 *
 * No es una medida, es el único dato que hay: en ese evento la persona tocó
 * "Reintentar" ~3 s después de la pantalla de error y la app cargó. 5 s cubre
 * ese hueco sin que el spinner parezca colgado.
 */
export const LAST_CHANCE_DELAY_MS = 5_000;

/**
 * True si el navegador se declara sin conexión.
 *
 * v6.11.67: un chunk que no carga SIN conexión no es un chunk obsoleto — es una
 * red caída. La diferencia importa porque el remedio es opuesto: ante un chunk
 * obsoleto hay que recargar para tomar assets nuevos; sin conexión, recargar es
 * lo PEOR que se puede hacer (se pierde la app que ya estaba corriendo y la
 * recarga tampoco va a poder traer nada).
 */
export function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/** Qué pasó la última vez que la recuperación decidió NO recargar. */
export interface ChunkReloadDiagnostics {
  /** Quién pidió la recarga: 'lazyImport' | 'errorBoundary' | … */
  reason: string;
  /** Etapa en la que se rindió. 0 = ni se intentó, por estar sin conexión. */
  stage: number;
  /** ¿El navegador estaba sin conexión al rendirse? */
  offline: boolean;
  /** ms transcurridos desde la primera recarga de la tanda (null si no hubo). */
  sinceFirstReloadMs: number | null;
}

// v6.11.67: cuando la recuperación se rinde, el error sube al ErrorBoundary y
// termina en Sentry SIN decir en qué etapa murió — y ahí no se puede distinguir
// "el deploy dejó los assets rotos" de "la conexión del celular se cayó". Esto
// guarda ese contexto para que beforeSend lo adjunte al evento.
let lastGiveUp: ChunkReloadDiagnostics | null = null;

/** Contexto de la última vez que la recuperación se rindió (null si nunca). */
export function getChunkReloadDiagnostics(): ChunkReloadDiagnostics | null {
  return lastGiveUp;
}

// v6.11.76 — ¿ya disparamos una recarga en ESTA carga de página?
//
// `window.location.reload()` no corta la ejecución: la página sigue viva hasta
// que el navegador la reemplaza. Y la etapa 2 es peor todavía, porque
// purgeCachesAndReload() **espera hasta 3 segundos** purgando caches ANTES de
// recargar. En toda esa ventana la app sigue corriendo, con varios chunks lazy
// en vuelo (Layout, la pantalla del router y RulebookReacceptanceGate se
// resuelven en paralelo).
//
// Sin esta bandera, cada uno de esos chunks que fallaba volvía a entrar acá y
// gastaba UNA ETAPA MÁS. Tres fallos concurrentes quemaban las tres etapas —
// 1, 2 y 3— sin que se hubiera completado NI UNA recarga: la app se rendía,
// mostraba la pantalla de error y lo reportaba a Sentry como "chunk obsoleto
// persistente tras recarga + purga de caches", una frase que describía algo que
// nunca había pasado.
//
// Así se leían LIGA-AJUPAM-51 y -52: `stage: 3` con `sinceFirstReloadMs` de
// 1552 y 3106 ms. Dos recargas completas de la SPA en iOS —cada una con su
// backoff de reintentos de ~1s— más los 3s de la purga no entran en 3,1
// segundos. El número no medía tres cargas de página: medía una sola, con la
// espera de la purga adentro.
//
// Es una variable de módulo a propósito: muere con la página, que es
// exactamente el ciclo de vida que le corresponde. El contador que sí tiene que
// sobrevivir a la recarga sigue viviendo en sessionStorage.
let reloadInFlight = false;

/**
 * Recuperación en TRES etapas ante un chunk obsoleto (guard anti-loop compartido).
 * Las etapas se cuentan por RECARGA, no por fallo — ver `reloadInFlight`:
 *   1ª recarga → recarga simple (alcanza cuando el host ya sirve el HTML nuevo).
 *   2ª recarga dentro de la ventana → purga los caches del Service Worker (el SW
 *      cachea '/' al instalarse → ese shell viejo puede servirse ante red
 *      inestable y referenciar chunks que ya no existen; la recarga simple no lo
 *      cura) + lo desregistra + recarga.
 *   3ª recarga → espera LAST_CHANCE_DELAY_MS y recarga una última vez: lo que
 *      sobrevive a la purga ya no es un shell viejo sino un bache (de red o del
 *      CDN recién deployado), y a un bache lo cura el tiempo, no otra purga.
 *   4º intento → nos rendimos y propagamos para que el error SÍ se reporte.
 *
 * Sin conexión no se recarga NUNCA, en ninguna etapa: ver isOffline().
 *
 * Devuelve true si hay una recarga en marcha —recién disparada, programada o ya
 * en curso—, o sea, si el caller puede quedarse esperando en vez de propagar.
 */
export function reloadForStaleChunk(reason: string): boolean {
  // Ya hay una recarga en vuelo: este fallo es parte del MISMO episodio, no de
  // uno nuevo. Sale antes de tocar el contador para no gastar etapas de más.
  if (reloadInFlight) return true;

  if (isOffline()) {
    lastGiveUp = { reason, stage: 0, offline: true, sinceFirstReloadMs: null };
    console.warn(`[App] fallo de chunk (${reason}) sin conexión → no se recarga`);
    return false;
  }
  let stage = 1;
  try {
    const now = Date.now();
    const last = Number(sessionStorage.getItem(RELOAD_TS_KEY) || '0');
    const withinWindow = now - last < RELOAD_WINDOW_MS;
    stage = withinWindow ? Number(sessionStorage.getItem(RELOAD_STAGE_KEY) || '1') + 1 : 1;
    if (stage > 3) {
      // Ojo: `last` es la recarga ANTERIOR (la etapa 3), no la primera. Medir
      // desde ahí reportaba ~1,5s en Sentry y hacía leer "la recuperación entera
      // duró un segundo y medio" cuando en realidad ese era solo el tramo final.
      const first = Number(sessionStorage.getItem(RELOAD_FIRST_TS_KEY) || '0') || last;
      lastGiveUp = {
        reason,
        stage,
        offline: false,
        sinceFirstReloadMs: first ? now - first : null,
      };
      console.error(`[App] chunk obsoleto persistente (${reason}) tras recarga, purga de caches y un último intento con espera; no se recarga de nuevo`);
      return false;
    }
    sessionStorage.setItem(RELOAD_TS_KEY, String(now));
    if (stage === 1) sessionStorage.setItem(RELOAD_FIRST_TS_KEY, String(now));
    sessionStorage.setItem(RELOAD_STAGE_KEY, String(stage));
  } catch {
    // sessionStorage puede fallar en modo privado; recargamos igual (sin guard).
  }
  reloadInFlight = true;
  if (stage === 1) {
    console.warn(`[App] chunk obsoleto (${reason}) → recargando para tomar assets nuevos`);
    window.location.reload();
    return true;
  }
  if (stage === 2) {
    console.warn(`[App] chunk obsoleto (${reason}) persiste tras recarga → purgando caches del SW y recargando`);
    void purgeCachesAndReload();
    return true;
  }
  console.warn(`[App] chunk obsoleto (${reason}) persiste tras la purga → último intento en ${LAST_CHANCE_DELAY_MS / 1000}s`);
  setTimeout(lastChanceReload, LAST_CHANCE_DELAY_MS);
  return true;
}

/**
 * La recarga de la etapa 3, cuando termina la espera.
 *
 * Refresca el timestamp de la etapa antes de recargar: la ventana anti-loop mide
 * de recarga a recarga, y si se quedara con el instante en que se PROGRAMÓ, la
 * espera se comería 5 de sus 30 segundos y una carga lenta podría caer fuera,
 * reiniciar en la etapa 1 y convertir la recuperación en un loop.
 *
 * Si justo se cortó la conexión, no recarga a ciegas (la recarga no traería
 * nada y tiraría abajo lo que se ve): espera a que vuelva.
 */
function lastChanceReload(): void {
  if (isOffline()) {
    window.addEventListener('online', lastChanceReload, { once: true });
    return;
  }
  try {
    sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now()));
  } catch {
    // sessionStorage puede fallar en modo privado; recargamos igual.
  }
  window.location.reload();
}

/**
 * Recarga pedida explícitamente por la persona (botón de la pantalla de error).
 * Va sin guard anti-loop —el guard existe para frenar recargas AUTOMÁTICAS, no
 * para discutirle a alguien que toca "Reintentar"— y de paso limpia el contador
 * para que la recuperación automática vuelva a tener su presupuesto completo.
 */
export function forceRecoveryReload(): void {
  try {
    sessionStorage.removeItem(RELOAD_TS_KEY);
    sessionStorage.removeItem(RELOAD_FIRST_TS_KEY);
    sessionStorage.removeItem(RELOAD_STAGE_KEY);
  } catch {
    // sessionStorage puede fallar en modo privado; recargamos igual.
  }
  reloadInFlight = true;
  void purgeCachesAndReload();
}

/**
 * Borra todos los caches del SW (incluido el shell '/' cacheado en la
 * instalación) y DESREGISTRA el Service Worker antes de recargar. Best-effort
 * con timeout: pase lo que pase, la página se recarga.
 *
 * v6.11.76 — Antes acá se hacía `registration.update()`, y esta etapa no
 * recuperaba nada. `update()` baja el sw.js nuevo pero lo deja en `waiting`:
 * el Service Worker VIEJO sigue siendo el controller de la página. En la
 * recarga vuelve a contestar él y, como los caches se acaban de borrar, los
 * repuebla desde su propia versión — con el mismo manifiesto viejo que pide el
 * chunk que ya no existe. El fallo sobrevivía a la etapa 2 intacto.
 *
 * Desregistrando no queda controller, así que la siguiente carga va a la red
 * directo y `index.tsx` registra el sw.js nuevo. Se pierde el offline por UNA
 * carga, que es un precio obviamente menor que quedarse con la app rota.
 *
 * Diagnóstico original en el PR #140 (Sentry LIGA-AJUPAM-4K: 10 usuarios, todos
 * iOS/Safari, con picos los días de deploy). La zona se reescribió entre medio
 * (#157, #164, #166) pero el `update()` sobrevivió a las tres pasadas.
 */
async function purgeCachesAndReload(): Promise<void> {
  try {
    const timeout = new Promise<void>((resolve) => setTimeout(resolve, 3_000));
    const purge = (async () => {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.allSettled(keys.map((k) => caches.delete(k)));
      }
      const regs = (await navigator.serviceWorker?.getRegistrations?.()) ?? [];
      await Promise.allSettled(regs.map((r) => r.unregister()));
    })();
    await Promise.race([purge, timeout]);
  } catch {
    // best-effort: recargamos igual
  }
  window.location.reload();
}

// Firmas de error que delatan un chunk/vendor obsoleto (no un bug de la app).
// Van en DOS regex a propósito, porque necesitan sensibilidad distinta:
//
// 1) Mensajes textuales del navegador. La redacción y el capitalizado varían
//    entre motores y versiones, así que se matchean sin distinguir mayúsculas.
const STALE_CHUNK_MESSAGE_RE = new RegExp(
  [
    'ChunkLoadError',
    'Loading chunk [\\w-]+ failed',
    'Failed to fetch dynamically imported module',
    'error loading dynamically imported module',
    'Importing a module script failed',
    // v6.11.149: la marca que emite NUESTRO Service Worker cuando el host
    // devuelve el fallback SPA para un /assets/*.js (v6.11.74). Faltaba en esta
    // lista, así que un chunk obsoleto que llegaba con ESTA redacción —y no con
    // la del navegador— no se reconocía como tal: sin conexión no lo descartaba
    // el filtro de v6.11.67 y el evento subía sin el contexto de recuperación.
    '\\[SW\\] fallback HTML para un chunk',
    '(useState|useEffect|useRef|useMemo|useCallback|useContext|useReducer|useLayoutEffect|React|_jsx|jsxRuntime) is not defined',
  ].join('|'),
  'i'
);

// 2) Lecturas de undefined donde lo único que separa un chunk obsoleto de un bug
//    real es el CAPITALIZADO del símbolo: `e.TeamView` es un componente lazy que
//    no vino en el chunk; `e.current_team_id` es un dato que llegó vacío — un bug
//    nuestro, que no se arregla recargando. Por eso esta va SIN el flag `i`:
//    v6.11.74, antes ambas listas compartían un único regex con 'i' y el flag
//    anulaba el `[A-Z]`, así que cualquier "undefined is not an object" caía del
//    lado de "chunk obsoleto" → la app se recargaba sola ante un bug de verdad y,
//    si además estaba sin conexión, beforeSend lo descartaba y nunca llegaba a
//    Sentry.
const STALE_CHUNK_SYMBOL_RE = new RegExp(
  [
    "undefined is not an object \\(evaluating '[\\w$]+\\.[A-Z][\\w$]*'\\)",
    "Cannot read properties of undefined \\(reading '[A-Z][\\w$]*'\\)",
  ].join('|')
);

/** Mensaje de un error, venga como string, como Error o como cualquier otra cosa. */
function messageOf(error: unknown): string {
  return typeof error === 'string'
    ? error
    : (error as { message?: string })?.message || String(error ?? '');
}

/** True si el error parece de chunk/vendor obsoleto (recuperable con recarga). */
export function isStaleChunkError(error: unknown): boolean {
  const msg = messageOf(error);
  return STALE_CHUNK_MESSAGE_RE.test(msg) || STALE_CHUNK_SYMBOL_RE.test(msg);
}

/**
 * True si el error es el de leer un export que NO vino en el módulo: el chunk
 * 404 y el host devolvió el HTML fallback, así que el import resolvió vacío y el
 * `.then(m => ({ default: m.X }))` del call site explotó al dereferenciarlo.
 *
 * Es la MITAD de `isStaleChunkError` a propósito, y la distinción importa:
 * ese error dice "el archivo no existe" (reintentar es inútil, hay que tomar
 * assets nuevos), mientras que la otra mitad —"Failed to fetch dynamically
 * imported module"— dice "el archivo existe y el fetch falló" (reintentar es
 * exactamente lo que corresponde). Usar el predicado completo acá haría que un
 * bache de red móvil se llevara una recarga de la app en vez de un reintento.
 */
export function isMissingExportError(error: unknown): boolean {
  return STALE_CHUNK_SYMBOL_RE.test(messageOf(error));
}
